import { type FC, useCallback, useMemo } from "react"
import { Navigate, useParams } from "react-router"

import EditStudentEmail, { type EmailSaveData } from "~/components/EditStudentEmail"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const EditLeadFormEmail: FC = () => {
	const formKey = useParams().formKey ?? ""

	const user = useUser().user!
	const platform = usePlatform()!

	const { data: layout, error: layoutError } = query.studentEmails.getLayout.useQuery()
	const { data: email, error: emailError, refetch } = query.studentEmails.getLeadForm.useQuery(formKey)

	const isNewsletter = email?.form?.kind === "newsletter"
	const placeholders = useMemo(
		() => [
			{ key: "email", value: user.email },
			...(isNewsletter
				? []
				: [
						{ key: "name", value: `${user.firstName} ${user.lastName}`.trim() },
						{ key: "message", value: "Hi, I would like to know more about your courses." },
					]),
			{ key: "formTitle", value: email?.form?.title ?? "Send us a message" },
			{ key: "pageTitle", value: email?.form?.pages[0]?.title ?? "Home" },
		],
		[email?.form, isNewsletter, user],
	)

	const handleSave = useCallback(
		async (data: EmailSaveData) => {
			if (data === null) return
			await trpc.studentEmails.updateLeadForm.mutate({
				formKey,
				data: { title: data.title, content: data.content },
			})
			await refetch()
		},
		[formKey, refetch],
	)

	if (!platform.plan.access.landingPage) {
		return <Navigate to={routes.settings.studentEmail.index()} replace />
	}

	return (
		<Layout>
			<PageBreadcrumb
				title={email ? (email.form?.title ?? "Website form") : "..."}
				path={[
					{ title: "Settings", link: routes.settings.index() },
					{ title: "Email Automation", link: routes.settings.studentEmail.index() },
					{ title: "Website form replies", link: routes.settings.studentEmail.leadForm() },
				]}
			/>
			{layoutError || emailError ? (
				<p>Failed to load the reply email.</p>
			) : !layout || !email ? (
				<PartialLoadingPage />
			) : (
				<div className="flex flex-col gap-4">
					<p className="text-gray-600">
						{email.form
							? `Sent to everyone who submits the "${email.form.title}" form on ${email.form.pages.map(page => page.title).join(", ")}, right after they submit it.`
							: "Sent to everyone who submits this form, right after they submit it. Save your website with the form on it for the reply to start going out."}
					</p>
					<EditStudentEmail
						email={email}
						placeholders={placeholders}
						layout={layout.content}
						aiTarget={{ type: "lead-form", formKey }}
						onSave={handleSave}
					/>
				</div>
			)}
		</Layout>
	)
}

export default EditLeadFormEmail
